import { SortConfiguration } from '../../../redux/ChartPoperties/ChartControlsInterface';
import { sortChartData } from '../../ChartAxes/chartDataFunctions/sortChartData';
import { filterShowHideData } from '../ShowHide/filterShowHideData';
import { compareWithLogicalOrder, isMonthOrWeekField } from '../Sort/dataOrdeingUtils';
import { updateChartData, SortChartData } from '../../../redux/ChartPoperties/ChartControlsActions';
import { getNameFromUid, getUidOfKey } from '../../CommonFunctions/CommonFunctions';

// Checks if a field should be disabled based on sorting rules
export const handleCheckDisableField = (
  fieldId: string,
  propKey: string,
  chartProperties: any,
  sortConfigurations: any,
) => {
  const chartAxes = chartProperties.properties[propKey].chartAxes;
  const tempDimensionFields =
    chartAxes?.find((axis: any) => axis.name === 'Dimension' || axis.name === 'Row')?.fields || [];
  let dimensionFields = JSON.parse(JSON.stringify(tempDimensionFields));
  const measureFields = chartAxes?.find((axis: any) => axis.name === 'Measure')?.fields || [];
  const splitByFields = chartAxes?.find((axis: any) => axis.name === 'Split By')?.fields || [];
  const distributionField =
    chartAxes?.find((axis: any) => axis.name === 'Distribution')?.fields[0] || {};

  const lastDimensionField =
    dimensionFields.length > 0 ? dimensionFields[dimensionFields.length - 1] : null;
  const splitByField = splitByFields.length > 0 ? splitByFields[0] : null;

  const isCurrentFieldMeasure = measureFields.some((field: any) => field.uId === fieldId);
  const isCurrentFieldLastDimension = lastDimensionField?.uId === fieldId;
  const isCurrentFieldSplitBy = splitByField?.uId === fieldId;
  const isCurrentFieldDistribution = distributionField && distributionField.uId === fieldId;

  // Always disable distribution field
  if (isCurrentFieldDistribution) {
    return true;
  }

  // Disable splitby fields except bar charts
  if (isCurrentFieldSplitBy) {
    if (
      ['multibar', 'horizontalBar', 'stackedBar', 'horizontalStacked']
        .includes(chartProperties.properties[propKey].chartType)
    ){
      return false;
    }
    return true;
  }

  // For line chart, area chart, and stacked area chart, disable measure
  if (
    ['line', 'area', 'stackedArea', 'calendar', 'sankey'].includes(
      chartProperties.properties[propKey].chartType,
    ) &&
    isCurrentFieldMeasure
  ) {
    return true;
  }

  // Check if any measure is sorted
  const isMeasureSorted = sortConfigurations.some((config: SortConfiguration) => {
    const isMeasure = measureFields.some((field: any) => field.uId === config.fieldId);
    return isMeasure && config.sortOrder !== '';
  });

  // case for cross tab and heat map: Cross tab or heat map - disable measure if any column field is present
  if (['crossTab', 'heatmap'].includes(chartProperties.properties[propKey].chartType)) {
    const columnFields = chartAxes?.find((axis: any) => axis.name === 'Column')?.fields || [];

    // Check if column axes have fields
    if (columnFields.length > 0) {
      if (isCurrentFieldMeasure) {
        return true;
      }
    }
  }

  // Table specific: If any row is sorted, disable all measures; if any measure is sorted, disable all rows
  if (chartProperties.properties[propKey].chartType === 'table') {
    const rowFields = chartAxes?.find((axis: any) => axis.name === 'Row')?.fields || [];

    // Check if current field is a row field
    const isCurrentFieldRow = rowFields.some((field: any) => field.uId === fieldId);

    // Check if any row field is sorted
    const isAnyRowFieldSorted = sortConfigurations.some((config: SortConfiguration) => {
      const isRowField = rowFields.some((field: any) => field.uId === config.fieldId);
      return isRowField && config.sortOrder !== '';
    });

    // Check if any measure field is sorted
    const isAnyMeasureFieldSorted = sortConfigurations.some((config: SortConfiguration) => {
      const isMeasureField = measureFields.some((field: any) => field.uId === config.fieldId);
      return isMeasureField && config.sortOrder !== '';
    });

    // If any row field is sorted, disable all measures
    if (isAnyRowFieldSorted && isCurrentFieldMeasure) {
      return true;
    }

    // If any measure field is sorted, disable all rows
    if (isAnyMeasureFieldSorted && isCurrentFieldRow) {
      return true;
    }
  }

  // Sankey specific: If one dimension is sorted, disable other dimensions
  if (chartProperties.properties[propKey].chartType === 'sankey') {
    const dimensionFieldsForSankey =
      chartAxes?.find((axis: any) => axis.name === 'Dimension')?.fields || [];

    // Check if current field is a dimension field
    const isCurrentFieldDimension = dimensionFieldsForSankey.some(
      (field: any) => field.uId === fieldId,
    );

    // Check if any dimension field is sorted (excluding the current field)
    const isAnyOtherDimensionSorted = sortConfigurations.some((config: SortConfiguration) => {
      const isDimensionField = dimensionFieldsForSankey.some(
        (field: any) => field.uId === config.fieldId,
      );
      const isDifferentField = config.fieldId !== fieldId;
      return isDimensionField && isDifferentField && config.sortOrder !== '';
    });

    // If another dimension is sorted, disable this dimension
    if (isCurrentFieldDimension && isAnyOtherDimensionSorted) {
      return true;
    }
  }

  // Barcharts specific: Disable measure for sorting if any splitby field is present
  if (
    ['multibar', 'horizontalBar', 'stackedBar', 'horizontalStacked']
      .includes(chartProperties.properties[propKey].chartType)
  ) {
    // If any split by field is available, disable all measures
    if (splitByFields.length && isCurrentFieldMeasure) {
      return true;
    }

    // If multiple measures are available, disable split by field
    if (measureFields.length > 1 && isCurrentFieldSplitBy) {
      return true;
    }

    // If any measure is sorted, disable split by field
    if (isMeasureSorted && isCurrentFieldSplitBy) {
      return true;
    }
  }

  // Check if last dimension is sorted
  const lastDimensionConfig = sortConfigurations.find(
    (config: SortConfiguration) => config.fieldId === lastDimensionField?.uId,
  );
  const isLastDimensionSorted = lastDimensionConfig && lastDimensionConfig.sortOrder !== '';

  // Rule 1: If last dimension is sorted, disable measure(s)
  if (isLastDimensionSorted && isCurrentFieldMeasure) {
    return true;
  }

  // Rule 2: If measure is sorted, disable last dimension
  if (isMeasureSorted && isCurrentFieldLastDimension) {
    return true;
  }

  // Rule 3: if a measure is sorted then disable other measures
  if (isCurrentFieldMeasure) {
    const isAnotherMeasureSorted = sortConfigurations.some((config: SortConfiguration) => {
      const isMeasure = measureFields.some((field: any) => field.uId === config.fieldId);
      const isDifferentField = config.fieldId !== fieldId;
      return isMeasure && isDifferentField && config.sortOrder !== '';
    });
    if (isAnotherMeasureSorted) {
      return true;
    }
  }

  return false;
};

// Function to get all field names of an axis
export const getFieldNamesByAxis = (chartAxes: any[] | undefined, axisName: string): string[] => {
  if (!chartAxes) return [];

  const axis = chartAxes.find((axis) => axis.name === axisName);
  return axis?.fields?.map((field: any) => field.displayname) ?? [];
};

export const getFieldUidsByAxis = (chartAxes: any[] | undefined, axisName: string): string[] => {
  if (!chartAxes) return [];

  const axis = chartAxes.find((axis) => axis.name === axisName);
  return axis?.fields?.map((field: any) => field.uId) ?? [];
};

// Function to extract data of specific fields
export const extractFieldsData = <T extends Record<string, any>>(
  data: T[],
  fieldNames: (keyof T | string)[],
): Partial<T>[] => {
  return data.map((item) => {
    const extracted: Partial<T> = {};

    fieldNames.forEach((field) => {
      if (field in item) {
        (extracted as Record<string, any>)[field as string] = item[field as string];
      }
    });

    return extracted;
  });
};

/*
 * Helper function to compare two records based on the provided sort configuration.
 * Supports ascending, descending, and manual sort orders.
 * Falls back to logical ascending sort when no configuration is provided.
 */
export const handleSort = (
  config: SortConfiguration | undefined,
  fieldName: string,
  a: any,
  b: any,
) => {
  let compareResult = 0;
  if (!config) {
    // Fallback to logical ascending order when no sort config exists
    compareResult = compareWithLogicalOrder(a[fieldName], b[fieldName], true);
  } else if (config.sortOrder === 'Manual Sort') {
    if (config.manualSortOrderObject) {
      const valA = a[fieldName];
      const valB = b[fieldName];
      const orderA = config.manualSortOrderObject[valA] ?? 999999;
      const orderB = config.manualSortOrderObject[valB] ?? 999999;
      compareResult = orderA - orderB;
    } else {
      // Sorting in ascending order if no manual sort order is available
      compareResult = compareWithLogicalOrder(a[fieldName], b[fieldName], true);
    }
  } else {
    const isAscending = config.sortOrder === 'Ascending';
    compareResult = compareWithLogicalOrder(a[fieldName], b[fieldName], isAscending);
  }

  return compareResult;
};

// Helper function to apply default month/weekday sorting
export const applyDefaultDateSorting = (data: any[], chartAxes: any[]) => {
  if (!data || data.length === 0) return data;

  // Find if any dimension field contains month/weekday data
  const axes = chartAxes;
  let dateFieldName: string | null = null;

  for (const axis of axes) {
    if (axis.name !== 'Measure' && axis.name !== 'Filter' && axis.fields) {
      for (const field of axis.fields) {
        if (isMonthOrWeekField(data, field.fieldname)) {
          dateFieldName = field.fieldname;
          break;
        }
      }
      if (dateFieldName) break;
    }
  }

  // If a month/weekday field is found, apply logical ascending sort
  if (dateFieldName) {
    return [...data].sort((a, b) =>
      compareWithLogicalOrder(a[dateFieldName], b[dateFieldName], true),
    );
  }

  return data;
};

// Function to sort data of a specific axes(eg. Row, Column)
export const sortAxisData = (
  axisFieldsData: any[],
  axisFieldNames: string[],
  sortConfigurations: SortConfiguration[],
  chartAxes?: any,
) => {
  const validConfigs = sortConfigurations.filter((config) => config.sortOrder);

  return [...axisFieldsData].sort((a, b) => {
    for (let i = 0; i < axisFieldNames.length; i++) {
      const fieldName = axisFieldNames[i];
      const fieldUid = getUidOfKey(chartAxes, fieldName);
      const config = validConfigs.find((config) => config.fieldId === fieldUid);
      let result = handleSort(config, fieldName, a, b);
      if (result !== 0) return result;
    }
    return 0;
  });
};

// Function to make an array unique
const uniqueByDeepValue = <T>(arr: T[]): T[] => {
  const seen = new Set<string>();
  return arr.filter((item) => {
    const key = JSON.stringify(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Function to filter matched data
function filterBySearch(data: any[], search: any): any[] {
  return data.filter((obj) => Object.entries(search).every(([key, value]) => obj[key] === value));
}

// Creates a stable string key by sorting object keys first
function createKey(obj: Record<string, any>) {
  return JSON.stringify(
    Object.keys(obj)
      .sort()
      .reduce((acc: any, key) => {
        acc[key] = obj[key];
        return acc;
      }, {}),
  );
}

// Function to merge sorted row and column data
export const mergeSortedData = (
  chartData: any[],
  sortedRowData: any[],
  sortedColumnData: any[],
  columnFieldNames: string[],
) => {
  const mergedData = [];
  const uniqueRowFields = uniqueByDeepValue(sortedRowData);
  const uniqueColumnFields = uniqueByDeepValue(sortedColumnData);

  for (let i = 0; i < uniqueRowFields.length; i++) {
    let matchedByRowFields = filterBySearch(chartData, uniqueRowFields[i]);

    const matchedRowMap = new Map<string, any>();
    for (let j = 0; j < matchedByRowFields.length; j++) {
      let colFields: any = {};

      columnFieldNames.forEach((field) => {
        if (field in matchedByRowFields[j]) {
          colFields[field] = matchedByRowFields[j][field];
        }
      });

      const key = createKey(colFields);
      matchedRowMap.set(key, matchedByRowFields[j]);
    }

    for (let k = 0; k < uniqueColumnFields.length; k++) {
      const key = createKey(uniqueColumnFields[k]);

      if (matchedRowMap.has(key)) {
        mergedData.push(matchedRowMap.get(key));
      }
    }
  }

  return mergedData;
};

export const multiFieldSort = ({
  data,
  sortConfigurations,
  chartProperties,
  propKey,
  rowFieldsData,
  columnFieldsData,
  rowFieldNames,
  columnFieldNames,
  dimensionFieldNames,
  splitByFieldNames,
  measureFieldUids,
}: {
  data: any[];
  sortConfigurations: SortConfiguration[];
  chartProperties: any;
  propKey: string;
  rowFieldsData: any[];
  columnFieldsData: any[];
  rowFieldNames: string[];
  columnFieldNames: string[];
  dimensionFieldNames: string[];
  splitByFieldNames: string[];
  measureFieldUids: string[];
}) => {
  const validConfigs = sortConfigurations.filter((config) => config.sortOrder);

  // Sorting ascending by default if no valid config is available
  if (!validConfigs.length) {
    const allFields = [
      ...rowFieldNames,
      ...columnFieldNames,
      ...dimensionFieldNames
    ]

    let result = [...data].sort((a, b) => {
      for (let i = 0; i < allFields.length; i++) {
        let compareResult = 0;
        const fieldName = allFields[i];
        compareResult = handleSort(undefined, fieldName, a, b);
        if (compareResult !== 0) {
          return compareResult;
        }
      }
      return 0;
    });
    return result;
  }

  const chartAxes = chartProperties.properties[propKey].chartAxes;

  let sortedData = [...data];

  const measureFieldUidUsedForSorting =
    validConfigs.find(
      (config) =>
        measureFieldUids.includes(config.fieldId) &&
        config.sortOrder &&
        config.sortOrder !== 'Manual Sort',
    )?.fieldId ?? null;

  const measureFieldUsedForSorting = measureFieldUidUsedForSorting
    ? getNameFromUid(chartAxes, measureFieldUidUsedForSorting)
    : null;

  const chartType = chartProperties.properties[propKey].chartType;

  if (chartType === 'crossTab') {
    // Crosstab  requires a custom sorting strategy due to its row–column structure
    const sortedRowData = sortAxisData(rowFieldsData, rowFieldNames, sortConfigurations, chartAxes);
    const sortedColumnData = sortAxisData(
      columnFieldsData,
      columnFieldNames,
      sortConfigurations,
      chartAxes,
    );

    if (rowFieldNames.length && columnFieldNames.length) {
      sortedData = mergeSortedData(sortedData, sortedRowData, sortedColumnData, columnFieldNames);
    } else if (rowFieldNames.length && !columnFieldNames.length) {
      // Sorting all the available rows if the current sorted value is in row and there is no column fields

      // Removing sorting for last row to make measure sort visible
      const effectiveRowSortFields = measureFieldUsedForSorting
        ? rowFieldNames.slice(0, -1)
        : [...rowFieldNames];

      const effectiveSortFields = measureFieldUsedForSorting
        ? [...effectiveRowSortFields, measureFieldUsedForSorting]
        : [...rowFieldNames];

      sortedData = [...sortedData].sort((a, b) => {
        for (let i = 0; i < effectiveSortFields.length; i++) {
          let compareResult = 0;
          const fieldName = effectiveSortFields[i];
          const fieldUid = getUidOfKey(chartAxes, fieldName);
          const config = validConfigs.find(
            (config: SortConfiguration) => config.fieldId === fieldUid,
          );

          compareResult = handleSort(config, fieldName, a, b);

          if (compareResult !== 0) {
            return compareResult;
          }
        }
        return 0;
      });
    } else if (!rowFieldNames.length && columnFieldNames.length) {
      // Sorting all the available columns if the current sorted value is in column and there is no row

      sortedData = [...sortedData].sort((a, b) => {
        for (let i = 0; i < columnFieldNames.length; i++) {
          let compareResult = 0;
          const fieldName = columnFieldNames[i];
          const fieldUid = getUidOfKey(chartAxes, fieldName);
          const config = validConfigs.find(
            (config: SortConfiguration) => config.fieldId === fieldUid,
          );

          compareResult = handleSort(config, fieldName, a, b);

          if (compareResult !== 0) {
            return compareResult;
          }
        }
        return 0;
      });
    }
  } else if (['multibar', 'horizontalBar', 'stackedBar', 'horizontalStacked'].includes(chartType)) {
    // If sorting by any measure value, removing sorting for last row to make measure sort visible
    const effectiveDimensionSortFields = measureFieldUsedForSorting
      ? dimensionFieldNames.slice(0, -1)
      : [...dimensionFieldNames];

    const effectiveSortFields = measureFieldUsedForSorting
      ? [...effectiveDimensionSortFields, measureFieldUsedForSorting]
      : [...dimensionFieldNames, ...splitByFieldNames];

    sortedData = [...sortedData].sort((a, b) => {
      for (let i = 0; i < effectiveSortFields.length; i++) {
        let compareResult = 0;
        const fieldName = effectiveSortFields[i];
        const fieldUid = getUidOfKey(chartAxes, fieldName);
        const config = validConfigs.find(
          (config: SortConfiguration) => config.fieldId === fieldUid,
        );
        compareResult = handleSort(config, fieldName, a, b);

        if (compareResult !== 0) {
          return compareResult;
        }
      }
      return 0;
    });
  } else if (chartType === 'heatmap') {
    const sortedRowData = sortAxisData(rowFieldsData, rowFieldNames, sortConfigurations, chartAxes);
    const sortedColumnData = sortAxisData(
      columnFieldsData,
      columnFieldNames,
      sortConfigurations,
      chartAxes,
    );

    sortedData = mergeSortedData(sortedData, sortedRowData, sortedColumnData, columnFieldNames);
  } else if (chartType === 'table') {
    // Sorting every field by ascending (default)
    sortedData = [...sortedData].sort((a, b) => {
      for (let i = 0; i < rowFieldNames.length; i++) {
        let compareResult = 0;
        const fieldName = rowFieldNames[i];
        compareResult = handleSort(undefined, fieldName, a, b);
        if (compareResult !== 0) {
          return compareResult;
        }
      }
      return 0;
    });

    // Sorting according to sort configs
    sortedData = [...sortedData].sort((a, b) => {
      for (const config of validConfigs) {
        let compareResult = 0;
        let fieldId = config.fieldId;
        let fieldName = getNameFromUid(chartAxes, fieldId);
        compareResult = handleSort(config, fieldName, a, b);
        if (compareResult !== 0) {
          return compareResult;
        }
      }
      return 0;
    });
  } else {
    // Default sorting for other charts
    sortedData = [...sortedData].sort((a, b) => {
      for (const config of validConfigs) {
        let compareResult = 0;
        const fieldName = getNameFromUid(chartAxes, config.fieldId);

        compareResult = handleSort(config, fieldName, a, b);

        if (compareResult !== 0) {
          return compareResult;
        }
      }
      return 0;
    });
  }

  return sortedData;
};

export const applyMultiFieldSort = ({
  data,
  sortConfigurations,
  chartControls,
  chartProperties,
  propKey,
  showHide,
  dispatch,
  rowFieldsData,
  columnFieldsData,
  rowFieldNames,
  columnFieldNames,
  dimensionFieldNames,
  splitByFieldNames,
  measureFieldUids,
}: {
  data: any[];
  sortConfigurations: SortConfiguration[];
  chartControls: any;
  chartProperties: any;
  propKey: string;
  showHide: any;
  dispatch: any;
  rowFieldsData: any[];
  columnFieldsData: any[];
  rowFieldNames: string[];
  columnFieldNames: string[];
  dimensionFieldNames: string[];
  splitByFieldNames: string[];
  measureFieldUids: string[];
}) => {
  if (sortConfigurations.length === 0) {
    // Apply default sorting if no configurations
    if (typeof showHide?.numberInput === 'number' && showHide?.numberInput !== '') {
      filterShowHideData(
        showHide.operator,
        dispatch,
        chartControls.properties[propKey].serverData,
        propKey,
        showHide.numberInput,
        chartControls,
        chartProperties,
      );
      return;
    }
  }

  let sortedData = multiFieldSort({
    data,
    sortConfigurations,
    chartProperties,
    propKey,
    rowFieldsData,
    columnFieldsData,
    rowFieldNames,
    columnFieldNames,
    dimensionFieldNames,
    splitByFieldNames,
    measureFieldUids,
  });

  dispatch(updateChartData(propKey, sortedData));
  // dispatch(SortChartData(propKey, sortedData));
};
